// Client mirror of the SocialKit decor kit (apps/socialkit/src/core/visual/v2/decor.ts).
// Paths + sizing MUST stay identical to the worker so the editor canvas shows the exact
// mark the master renders. Keep in lockstep when authoring new marks.

import type { DecorLayerMark } from './types'

const PATHS: Record<
  DecorLayerMark,
  { viewBox: string; body: (color: string, sw: number) => string }
> = {
  squiggle: {
    viewBox: '0 0 220 44',
    body: (c, sw) =>
      `<path d="M8 28 Q 30 8, 52 24 T 96 24 T 140 24 T 184 24 Q 200 22, 212 14" fill="none" stroke="${c}" stroke-width="${sw}" stroke-linecap="round"/>`,
  },
  zigzag: {
    viewBox: '0 0 220 44',
    body: (c, sw) =>
      `<path d="M8 34 L 44 12 L 80 34 L 116 12 L 152 34 L 188 12 L 212 26" fill="none" stroke="${c}" stroke-width="${sw}" stroke-linecap="round" stroke-linejoin="round"/>`,
  },
  asterisk: {
    viewBox: '0 0 64 64',
    body: (c, sw) =>
      ['M32 6 L32 58', 'M9 19 L55 45', 'M55 19 L9 45']
        .map(
          d =>
            `<path d="${d}" fill="none" stroke="${c}" stroke-width="${sw}" stroke-linecap="round"/>`
        )
        .join(''),
  },
  arrow: {
    viewBox: '0 0 120 72',
    body: (c, sw) =>
      `<path d="M10 14 Q 70 6, 92 34 Q 104 50, 92 60" fill="none" stroke="${c}" stroke-width="${sw}" stroke-linecap="round"/>` +
      `<path d="M78 52 L 92 62 L 102 46" fill="none" stroke="${c}" stroke-width="${sw}" stroke-linecap="round" stroke-linejoin="round"/>`,
  },
  circle: {
    viewBox: '0 0 96 64',
    body: (c, sw) =>
      `<path d="M58 8 Q 10 6, 10 32 Q 10 56, 48 56 Q 86 56, 86 30 Q 86 12, 52 12" fill="none" stroke="${c}" stroke-width="${sw}" stroke-linecap="round"/>`,
  },
}

export const DECOR_MARKS = Object.keys(PATHS) as DecorLayerMark[]

export function isDecorMark(mark: string): mark is DecorLayerMark {
  return mark in PATHS
}

// The doc arrives as unvalidated JSON: a non-hex color reaching btoa() (unicode) would
// THROW and crash the whole canvas tree, and anything non-hex must fall to white exactly
// like the worker renderer does (parity).
const HEX_RE = /^#[0-9a-fA-F]{3,8}$/
const safeColor = (c: string): string => (HEX_RE.test(c) ? c : '#FFFFFF')

/** Data-uri SVG for a mark in the given color (stroke authored per viewBox scale). */
export function decorUri(mark: DecorLayerMark, color: string): string {
  const def = PATHS[mark]
  const [, , wRaw, hRaw] = def.viewBox.split(' ')
  const sw = Math.max(5, Math.round(Number(hRaw) * 0.16))
  const svg = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="${def.viewBox}" width="${wRaw}" height="${hRaw}">${def.body(safeColor(color), sw)}</svg>`
  return `data:image/svg+xml;base64,${btoa(svg)}`
}

/** The on-canvas PX box per mark for a canvas of w×h (worker decorBox mirror — heights key
 *  off canvas WIDTH so the authored SVG aspect holds on every aspect ratio). */
export function decorBox(
  mark: DecorLayerMark,
  canvasW: number,
  canvasH: number
): { w: number; h: number } {
  switch (mark) {
    case 'squiggle':
    case 'zigzag':
      return { w: Math.round(canvasW * 0.3), h: Math.round(canvasH * 0.045) }
    case 'asterisk':
      return { w: Math.round(canvasW * 0.085), h: Math.round(canvasW * 0.085) }
    case 'arrow':
      return { w: Math.round(canvasW * 0.14), h: Math.round(canvasW * 0.085) }
    case 'circle':
      return { w: Math.round(canvasW * 0.13), h: Math.round(canvasW * 0.087) }
  }
}

/** The loop's box + offsets for a run of `textLen` chars at `sizePx` (worker
 *  decor.ts::encircleBox mirror — ONE owner per side for the four magic constants). */
export function encircleBox(
  textLen: number,
  sizePx: number
): { w: number; h: number; top: number; left: number } {
  return {
    w: Math.round(textLen * sizePx * 0.52 + sizePx * 0.4),
    h: Math.round(sizePx * 1.2),
    top: -Math.round(sizePx * 0.1),
    left: -Math.round(sizePx * 0.2),
  }
}

/** The encircling loop, authored AT the target box size (worker encircleUri mirror). */
export function encircleUri(color: string, w: number, h: number): string {
  const sw = Math.max(6, Math.round(h * 0.05))
  const x = (f: number) => Math.round(w * f)
  const y = (f: number) => Math.round(h * f)
  const path = `M ${x(0.62)} ${y(0.12)} Q ${x(0.06)} ${y(0.04)}, ${x(0.06)} ${y(0.5)} Q ${x(0.06)} ${y(0.92)}, ${x(0.5)} ${y(0.92)} Q ${x(0.95)} ${y(0.92)}, ${x(0.95)} ${y(0.46)} Q ${x(0.95)} ${y(0.14)}, ${x(0.55)} ${y(0.16)}`
  const svg = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${w} ${h}" width="${w}" height="${h}"><path d="${path}" fill="none" stroke="${safeColor(color)}" stroke-width="${sw}" stroke-linecap="round"/></svg>`
  return `data:image/svg+xml;base64,${btoa(svg)}`
}
